

import { useState, useRef, useEffect } from 'react';

const FAQS = [
  {
    q: 'Czym zajmuje się Orsonet?',
    a: 'Orsonet to studio specjalizujące się we wdrażaniu nowoczesnych technologii AI dla firm. Tworzymy agentów AI na strony, realizujemy projekty zgodności EUDR wspierane AI, prowadzimy szkolenia, piszemy dedykowane oprogramowanie i budujemy strony internetowe premium z SEO.',
  },
  {
    q: 'Co to jest Agent AI na stronę i jak działa?',
    a: 'Agent AI to inteligentny asystent wbudowany w Twoją stronę, który obsługuje klientów 24/7. Odpowiada na pytania, kwalifikuje leady, umawia spotkania, zbiera dane kontaktowe — wszystko w naturalnym języku. Pracuje wtedy, gdy Twój zespół śpi.',
  },
  {
    q: 'Co to jest EUDR i jak AI pomaga w compliance?',
    a: 'EUDR (EU Deforestation Regulation) to unijne rozporządzenie wymagające udowodnienia, że produkty nie przyczyniają się do wylesienia. Wdrażamy systemy AI, które automatycznie analizują łańcuchy dostaw, gromadzą dokumentację i generują raporty zgodności — oszczędzając setki godzin pracy.',
  },
  {
    q: 'Dla kogo są szkolenia z AI?',
    a: 'Szkolenia prowadzimy dla firm każdej wielkości — od jednoosobowych działalności po duże przedsiębiorstwa. Uczymy, jak używać AI w codziennej pracy, automatyzować procesy, pisać prompty, integrować narzędzia AI z istniejącymi systemami i zwiększać efektywność zespołu.',
  },
  {
    q: 'Ile kosztuje wdrożenie Agenta AI?',
    a: 'Koszt zależy od złożoności i zakresu. Podstawowy agent AI zaczyna się od 2 500 zł. Rozbudowane systemy z integracjami, bazą wiedzy i obsługą wielojęzyczną to inwestycja od 8 000 zł. Zawsze przygotowujemy wycenę indywidualną — skontaktuj się po bezpłatną konsultację.',
  },
  {
    q: 'Czy tworzycie oprogramowanie AI dla konkretnych branż?',
    a: 'Tak — to nasza specjalność. Piszemy dedykowane programy AI dla konkretnych producentów i branż: analizę danych produkcyjnych, systemy rekomendacji, automatyzację dokumentacji, inteligentne dashboardy. Każde rozwiązanie jest szyte na miarę potrzeb klienta.',
  },
  {
    q: 'Czy pracujecie z firmami z Bieszczadów i całej Polski?',
    a: 'Tak — mamy doświadczenie zarówno z lokalnymi firmami z regionu Bieszczad (hotele, agroturystyka, restauracje, ośrodki), jak i z klientami z całej Polski i zagranicy. Większość pracy realizujemy zdalnie, ale w razie potrzeby spotykamy się osobiście.',
  },
];

function FaqItem({ q, a, index }: { q: string; a: string; index: number }) {
  const [open, setOpen] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (bodyRef.current) {
      setHeight(open ? bodyRef.current.scrollHeight : 0);
    }
  }, [open]);

  return (
    <div
      className="border-b border-white/8 last:border-0 transition-all duration-300"
      style={{ borderColor: open ? 'rgba(0,212,255,0.2)' : undefined }}
    >
      <button
        className="w-full flex items-center justify-between gap-4 py-6 text-left cursor-pointer group"
        onClick={() => setOpen((v) => !v)}
      >
        <div className="flex items-center gap-4">
          <span
            className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300"
            style={{
              background: open ? 'rgba(0,212,255,0.15)' : 'rgba(255,255,255,0.05)',
              color: open ? '#00d4ff' : 'rgba(255,255,255,0.3)',
              border: open ? '1px solid rgba(0,212,255,0.3)' : '1px solid rgba(255,255,255,0.1)',
            }}
          >
            {String(index + 1).padStart(2, '0')}
          </span>
          <span
            className="text-base font-semibold transition-colors duration-300"
            style={{ color: open ? '#ffffff' : 'rgba(255,255,255,0.75)' }}
          >
            {q}
          </span>
        </div>
        <div
          className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300"
          style={{
            background: open ? 'rgba(0,212,255,0.15)' : 'rgba(255,255,255,0.05)',
            border: open ? '1px solid rgba(0,212,255,0.3)' : '1px solid rgba(255,255,255,0.1)',
            transform: open ? 'rotate(45deg)' : 'rotate(0deg)',
          }}
        >
          <i className="ri-add-line text-sm" style={{ color: open ? '#00d4ff' : 'rgba(255,255,255,0.4)' }} />
        </div>
      </button>
      <div
        style={{ height: `${height}px`, overflow: 'hidden', transition: 'height 0.35s cubic-bezier(0.4,0,0.2,1)' }}
      >
        <div ref={bodyRef} className="pb-6 pl-11">
          <p className="text-white/55 text-sm leading-relaxed">{a}</p>
        </div>
      </div>
    </div>
  );
}

export default function FaqSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-32 bg-[#070707] relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left */}
          <div
            className="lg:sticky lg:top-32"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateX(0)' : 'translateX(-30px)',
              transition: 'opacity 0.7s ease, transform 0.7s ease',
            }}
          >
            <p className="text-[#00d4ff] text-xs uppercase tracking-[3px] font-semibold mb-4">FAQ</p>
            <h2 className="text-4xl md:text-5xl font-black text-white leading-tight mb-6">
              Często zadawane<br />
              <span className="text-white/40">pytania</span>
            </h2>
            <p className="text-white/45 text-base leading-relaxed mb-10">
              Odpowiadamy na najczęstsze pytania o AI i nasze usługi. Nie znalazłeś odpowiedzi? Napisz — odpowiemy w ciągu 24 godzin.
            </p>
            <a
              href="/kontakt"
              className="inline-flex items-center gap-2 px-7 py-4 rounded-xl font-bold text-black text-sm whitespace-nowrap cursor-pointer hover:scale-105 transition-transform shadow-[0_0_30px_rgba(0,212,255,0.25)]"
              style={{ background: 'linear-gradient(135deg, #00d4ff, #00b8e6)' }}
            >
              <i className="ri-message-3-line" />
              Zadaj pytanie
            </a>

            {/* Decorative */}
            <div
              className="mt-14 p-6 rounded-2xl border border-white/8 bg-white/[0.02]"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#00ff88]/10 border border-[#00ff88]/20">
                  <i className="ri-time-line text-[#00ff88] text-lg" />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">Szybka odpowiedź</p>
                  <p className="text-white/40 text-xs">Odpowiadamy w ciągu 24h</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#ff6b9d]/10 border border-[#ff6b9d]/20">
                  <i className="ri-shield-check-line text-[#ff6b9d] text-lg" />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">Bezpłatna konsultacja</p>
                  <p className="text-white/40 text-xs">Bez zobowiązań, konkretna rozmowa</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right: FAQ list */}
          <div
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateX(0)' : 'translateX(30px)',
              transition: 'opacity 0.7s ease 0.2s, transform 0.7s ease 0.2s',
            }}
          >
            {FAQS.map((item, i) => (
              <FaqItem key={i} q={item.q} a={item.a} index={i} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

